import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ensureFirebaseAuth,
  firebaseAuthEnabled,
  firebaseRuntimeConfigError,
  onAuthStateChanged,
  signOut,
  type User,
} from "../lib/firebase.ts";

export type AuthRole = "admin" | "basic";

interface AuthContextValue {
  user: User | null;
  role: AuthRole | null;
  isLoading: boolean;
  authError: string | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toRole(claimValue: unknown): AuthRole {
  return claimValue === "admin" ? "admin" : "basic";
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AuthRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!firebaseAuthEnabled) {
      setAuthError(
        firebaseRuntimeConfigError ??
          "Firebase auth is not configured for the frontend. Set FIREBASE_WEB_* variables and reload."
      );
      setIsLoading(false);
      return;
    }

    const auth = ensureFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      setAuthError(null);

      if (!nextUser) {
        setRole(null);
        setIsLoading(false);
        return;
      }

      try {
        const token = await nextUser.getIdTokenResult();
        setRole(toRole(token.claims.role));
      } catch {
        setRole("basic");
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const logout = useCallback(async () => {
    if (!firebaseAuthEnabled) return;
    await signOut(ensureFirebaseAuth());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      isLoading,
      authError,
      logout,
    }),
    [user, role, isLoading, authError, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
